import type { TutorAnalyticsSummary } from '../../../tutor/types/tutorAnalytics';
import type { AssignmentDashboardStats } from '../hooks/useAssignmentDashboardStats';

export type ActivityItem = {
  id: string;
  type: 'lesson' | 'homework' | 'tutor';
  title: string;
  at: Date;
};

/** Build timeline from tutor sessions and homework submissions that have real timestamps only. */
export function buildRecentActivities(params: {
  tutorAnalytics: TutorAnalyticsSummary | null;
  assignmentStats: AssignmentDashboardStats;
  /** Localized fallback label for tutor sessions without a lesson number. */
  genericTutorSessionLabel?: string;
}): ActivityItem[] {
  const genericLabel = params.genericTutorSessionLabel?.trim() || 'Tutor session';
  const items: ActivityItem[] = [];

  for (const session of params.tutorAnalytics?.session_history ?? []) {
    const at = session.ended_at || session.started_at;
    if (!at) continue;
    const date = new Date(at);
    if (Number.isNaN(date.getTime())) continue;
    const lessonNo = session.lesson_no;
    items.push({
      id: `tutor-${session.session_id}`,
      type: lessonNo != null ? 'lesson' : 'tutor',
      title:
        lessonNo != null
          ? `Lesson ${lessonNo}${session.topic_label ? ` — ${session.topic_label}` : ''}`
          : session.topic_label?.trim() || genericLabel,
      at: date,
    });
  }

  for (const fb of params.assignmentStats.recentFeedback) {
    const atRaw = fb.submittedAt;
    if (!atRaw) continue;
    const date = new Date(atRaw);
    if (Number.isNaN(date.getTime())) continue;
    items.push({
      id: `hw-${fb.title}-${atRaw}`,
      type: 'homework',
      title: `${fb.title} (score ${fb.grade})`,
      at: date,
    });
  }

  return items.sort((a, b) => b.at.getTime() - a.at.getTime()).slice(0, 8);
}
